/**
 * Utility functions for formatting and styling the "Last Updated" display
 */
import {
  MILISECONDS_TO_SECONDS,
  SECONDS_TO_MINUTES,
  TWELVE_HOUR_CLOCK_HOURS,
  MERIDIEM_SPLIT,
  STALENESS_THRESHOLDS
} from "./timeConstants";

// Time format thresholds (in seconds)
const TIME_FORMAT_THRESHOLDS = {
  JUST_NOW_MAX: 60,   // < 60s → "Just now"
  MIN_AGO_MAX: 360,   // 60s - 359s → "X min ago"
  HOURS_MAX: 86400    // 360s - 86400s → "H:MM am/pm"; > 86400s → date + time
};

// Display text constants
const DISPLAY_TEXT = {
  NO_UPDATE: 'No Update Available',
  JUST_NOW: 'Just now',
  MIN_AGO: 'min ago',
  STALE_TEXT: '- Check controller connection'
};

// CSS class names for staleness styling (defined in Dashboard.css)
const STALENESS_CLASSES = {
  GOOD: 'staleness-good',
  WARNING: 'staleness-warning',
  STALE: 'staleness-stale'
};

/**
 * Formats a Date object into a 12-hour clock time string.
 * @param {Date} date - The date object to format.
 * @returns {string} Time string in "H:MM am/pm" format (e.g., "2:05 pm").
 */
function formatTime(date) {
  const hours = date.getHours() % TWELVE_HOUR_CLOCK_HOURS || TWELVE_HOUR_CLOCK_HOURS;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = date.getHours() < TWELVE_HOUR_CLOCK_HOURS ? MERIDIEM_SPLIT.AM : MERIDIEM_SPLIT.PM;
  return `${hours}:${minutes} ${ampm}`;
}

/**
 * Formats a Date object into a MM/DD/YYYY date string.
 * @param {Date} date - The date object to format.
 * @returns {string} Date string in "MM/DD/YYYY" format (e.g., "02/20/2026").
 */
function formatDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Formats the timeReported value for display
 * @param {Date|null} timeReported - The timestamp from the API
 * @returns {string} Formatted time string
 */
function formatTimeReported(timeReported) {
  if (!timeReported) {
    return DISPLAY_TEXT.NO_UPDATE;
  }

  const elapsedSeconds = Math.floor((Date.now() - timeReported.getTime()) * MILISECONDS_TO_SECONDS);
  const staleText = elapsedSeconds >= STALENESS_THRESHOLDS.STALE_SECONDS
    ? DISPLAY_TEXT.STALE_TEXT
    : '';

  // < 60s: "Just now"
  if (elapsedSeconds < TIME_FORMAT_THRESHOLDS.JUST_NOW_MAX) {
    return DISPLAY_TEXT.JUST_NOW;
  }

  // 60s - 359s: "X min ago"
  if (elapsedSeconds < TIME_FORMAT_THRESHOLDS.MIN_AGO_MAX) {
    const elapsedMinutes = Math.floor(elapsedSeconds * SECONDS_TO_MINUTES);
    return `${elapsedMinutes} ${DISPLAY_TEXT.MIN_AGO}`;
  }

  // 360s - 86400s: "H:MM am/pm"
  if (elapsedSeconds <= TIME_FORMAT_THRESHOLDS.HOURS_MAX) {
    return `${formatTime(timeReported)} ${staleText}`.trim();
  }

  // > 86400s: "MM/DD/YYYY H:MM am/pm"
  return `${formatDate(timeReported)} ${formatTime(timeReported)} ${staleText}`.trim();
}

/**
 * Formats a sensor/relay timeLastUpdate for display.
 * Unlike formatTimeReported, does not append a staleness suffix.
 * @param {Date|null} date
 * @returns {string}
 */
function formatDeviceTimestamp(date) {
  if (!date) return DISPLAY_TEXT.NO_UPDATE;
  const elapsedSeconds = Math.floor((Date.now() - date.getTime()) * MILISECONDS_TO_SECONDS);
  if (elapsedSeconds < TIME_FORMAT_THRESHOLDS.JUST_NOW_MAX) return DISPLAY_TEXT.JUST_NOW;
  if (elapsedSeconds < TIME_FORMAT_THRESHOLDS.MIN_AGO_MAX) {
    return `${Math.floor(elapsedSeconds * SECONDS_TO_MINUTES)} ${DISPLAY_TEXT.MIN_AGO}`;
  }
  return formatTime(date);
}

/**
 * Returns the CSS class for staleness color coding
 * @param {Date|null} timeReported - The timestamp from the API
 * @returns {string} CSS class name for staleness styling
 */
function getTimeStalenessClass(timeReported) {
  if (!timeReported) {
    return '';
  }

  const now = new Date();
  const elapsedMs = now.getTime() - timeReported.getTime();
  const elapsedSeconds = Math.floor(elapsedMs * MILISECONDS_TO_SECONDS);

  if (elapsedSeconds < STALENESS_THRESHOLDS.WARNING_SECONDS) {
    return STALENESS_CLASSES.GOOD;
  }

  if (elapsedSeconds < STALENESS_THRESHOLDS.STALE_SECONDS) {
    return STALENESS_CLASSES.WARNING;
  }

  return STALENESS_CLASSES.STALE;
}

export {
  formatTimeReported,
  formatDeviceTimestamp,
  getTimeStalenessClass,
  STALENESS_THRESHOLDS,
  TIME_FORMAT_THRESHOLDS,
  DISPLAY_TEXT,
  STALENESS_CLASSES
};
